import { createContext, useCallback, useContext } from "react";
import { graphql, useMutation } from "react-relay";
import * as Haptic from "expo-haptics";
import { useSetFocusMutation } from "./__generated__/useSetFocusMutation.graphql";
import { Platform } from "react-native";

const MUTATION = graphql`
  mutation useSetFocusMutation(
    $storecastId: ID!
    $subject: StorecastFocusSubjectNewInput!
  ) {
    dashboard(dashboardId: $storecastId) {
      setNewFocus(subject: $subject) {
        subject {
          nodeId
          detailItemId
        }
      }
    }
  }
`;

type FocusContextType = (widgetId: string, detailItemId?: string) => void;

const CTX = createContext<FocusContextType | undefined>(undefined);

export function FocusProvider({
  children,
  focus,
}: {
  children: React.ReactNode;
  focus: FocusContextType;
}) {
  return <CTX.Provider value={focus}>{children}</CTX.Provider>;
}
export function useSetWidgetFocus(widgetId: string, storecastId?: string) {
  const [mutation] = useMutation<useSetFocusMutation>(MUTATION);

  const focus = useContext(CTX);
  const setSingleWidgetFocus = useCallback(
    (detailItemId?: string) => {
      if (!storecastId) {
        return;
      }
      if (Platform.OS === "ios") {
        Haptic.impactAsync(Haptic.ImpactFeedbackStyle.Medium);
      }
      if (focus) {
        focus(widgetId, detailItemId);
      } else {
        mutation({
          onError: (error) => {
            console.log(error);
          },
          optimisticUpdater: (store) => {
            const storecast = store.get(storecastId);
            if (!storecast) return;
            storecast.setValue(widgetId, "focusedNodeId");
            storecast.setValue(detailItemId, "focusedDetailItemId");
          },
          updater: (store) => {
            const storecast = store.get(storecastId);
            if (!storecast) return;
            storecast.setValue(widgetId, "focusedNodeId");
            storecast.setValue(detailItemId, "focusedDetailItemId");
          },
          variables: {
            storecastId,
            subject: {
              nodeId: widgetId,
              detailItemId,
            },
          },
        });
      }
    },
    [mutation, storecastId, widgetId, focus]
  );

  return setSingleWidgetFocus;
}
